// AppShell — sidebar layout used by every page in the dashboard.
//
// Desktop (≥ lg / 1024px): sidebar is permanently visible on the left.
// Mobile / tablet: sidebar is a slide-in drawer; opens via a hamburger
// button in the header, closes on link click or backdrop tap.

import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useDemoMode } from '../lib/use-demo-mode';
import { useCatalogFeedback } from '../lib/use-catalog-feedback';
import { DeadLetterBanner } from './dead-letter-banner';
import { CopilotDock, CopilotHeaderButton } from './copilot/copilot-panel';
import { UserButton, OrganizationSwitcher } from '@clerk/clerk-react';
import {
  LayoutDashboard,
  Wallet,
  FileText,
  Users,
  Building2,
  Grid3x3,
  AlertTriangle,
  Plane,
  Inbox,
  Package,
  Search,
  Radar,
  Menu,
  X,
  ShieldCheck,
  ClipboardCheck,
} from 'lucide-react';

interface NavItem {
  href: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Treat the route as active when the current path starts with `matchPrefix`.
   *  Keep `href` as the canonical destination. */
  matchPrefix?: string;
  /** Shown only when the workspace has this capability. */
  requires?: 'catalogFeedback';
}

/** The sidebar items this workspace may see. */
export function visibleNav(items: NavItem[], caps: { catalogFeedback: boolean }): NavItem[] {
  return items.filter((i) => i.requires !== 'catalogFeedback' || caps.catalogFeedback);
}

export const NAV: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard, matchPrefix: '/$' },
  { href: '/rfqs', label: 'RFQs', icon: FileText, matchPrefix: '/rfqs' },
  { href: '/alt-parts', label: 'Alt-part search', icon: Search, matchPrefix: '/alt-parts' },
  { href: '/orders', label: 'Orders', icon: Package, matchPrefix: '/orders' },
  { href: '/customers', label: 'Customers', icon: Users, matchPrefix: '/customers' },
  { href: '/vendors', label: 'Vendors', icon: Building2, matchPrefix: '/vendors' },
  { href: '/vendor-inbox', label: 'Vendor inbox', icon: Inbox, matchPrefix: '/vendor-inbox' },
  { href: '/matrix', label: 'Matrix', icon: Grid3x3, matchPrefix: '/matrix' },
  { href: '/coverage-gaps', label: 'Coverage gaps', icon: AlertTriangle, matchPrefix: '/coverage-gaps' },
  { href: '/catalog-review', label: 'Catalog review', icon: ClipboardCheck, matchPrefix: '/catalog-review', requires: 'catalogFeedback' },
  {
    href: '/partsbase-automation',
    label: 'PartsBase',
    icon: Radar,
    matchPrefix: '/partsbase-automation',
  },
  { href: '/compliance', label: 'Compliance', icon: ShieldCheck, matchPrefix: '/compliance' },
  { href: '/billing', label: 'Billing & settings', icon: Wallet, matchPrefix: '/billing' },
];

export interface AppShellProps {
  children: ReactNode;
  /** Optional in-page header content. Renders on the right of the page title bar. */
  headerActions?: ReactNode;
  /** Page title shown in the top bar. */
  title?: string;
  /** Page subtitle / breadcrumb. */
  subtitle?: ReactNode;
}

export function AppShell({ children, headerActions, title, subtitle }: AppShellProps) {
  const [location] = useLocation();
  const catalogFeedback = useCatalogFeedback();
  const [mobileOpen, setMobileOpen] = useState(false);
  const demoMode = useDemoMode();

  // Close drawer on route change (clicking a Link in the drawer).
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [mobileOpen]);

  return (
    <div className="h-dvh flex flex-col bg-background overflow-hidden">
      {/* Demo-environment banner — only on the demo deployment (DEMO_MODE=true). */}
      {demoMode && (
        <div className="flex-shrink-0 bg-amber-400 text-amber-950 text-xs sm:text-sm font-semibold text-center px-4 py-1.5">
          🟡 DEMO ENVIRONMENT — sample data only. No emails or messages are actually sent.
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
      {/* Backdrop — only visible on mobile when drawer is open */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50 transition-opacity"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed on mobile (slides in/out), static on desktop */}
      <aside
        className={`
          flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border
          fixed lg:static inset-y-0 left-0 z-40 w-64 lg:w-56 flex-shrink-0
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <div className="px-5 py-5 border-b border-sidebar-border flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center shadow-sm">
              <Plane className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-semibold text-sm tracking-tight text-white">APD</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-sidebar-foreground-muted">
                Platform
              </div>
            </div>
          </Link>
          {/* Close button — only on mobile */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden p-1 rounded text-sidebar-foreground-muted hover:text-white hover:bg-sidebar-hover"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {visibleNav(NAV, { catalogFeedback: catalogFeedback.canView }).map((item) => {
            const Icon = item.icon;
            const active =
              item.matchPrefix === '/$'
                ? location === '/'
                : location === item.href || location.startsWith(item.href + '/');
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  active
                    ? 'bg-sidebar-active text-sidebar-active-foreground shadow-sm'
                    : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={active ? 2.5 : 2} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-sidebar-border">
          <div className="flex items-center justify-between gap-2 px-2">
            <div className="flex-1 min-w-0">
              <OrganizationSwitcher
                hidePersonal
                appearance={{
                  elements: {
                    rootBox: 'w-full',
                    organizationSwitcherTrigger: 'text-sidebar-foreground hover:bg-sidebar-hover px-2 py-1.5 rounded text-xs',
                  },
                }}
              />
            </div>
            <UserButton
              appearance={{
                elements: { avatarBox: 'w-7 h-7' },
              }}
            />
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {(title || headerActions) && (
          // Wraps: when the page's actions do not fit beside the title (PartsBase has
          // Pause + Save, Compliance has two buttons), they drop to their own row
          // instead of squeezing the title down to "PartsBase Au…".
          <header className="flex-shrink-0 bg-surface border-b border-border px-4 lg:px-6 py-3 lg:py-3.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex items-center gap-2 min-w-0 max-w-full">
              {/* Hamburger — only on mobile */}
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden flex-shrink-0 p-1.5 -ml-1 rounded text-foreground-muted hover:text-foreground hover:bg-muted"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                {title && (
                  <h1 className="text-base lg:text-lg font-semibold tracking-tight truncate">{title}</h1>
                )}
                {subtitle && (
                  <div className="hidden sm:block text-xs text-foreground-muted mt-0.5 truncate">{subtitle}</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
              {headerActions}
              <CopilotHeaderButton />
            </div>
          </header>
        )}

        {/* Rendered here, not just imported: from 2026-09-11 until 2026-10-22 only
            the import existed, so the build tree-shook the banner away and no
            dead letter was ever shown. Renders nothing at zero or for non-admins. */}
        <DeadLetterBanner />

        <main className="flex-1 overflow-hidden min-w-0">{children}</main>
      </div>
      {/* APD Copilot: docked column on desktop, sheet / full screen below. */}
      <CopilotDock />
      </div>
    </div>
  );
}
